import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AssetClass, LotType, ResourceType } from '@/cip/_openapi/portfolio-optimizer';
import { useClosedLots, useOpenLots } from '@/cip/api/lot';
import { closedLotListColumns } from '@/cip/Lot/closedLotListColumns';
import { openLotListColumns } from '@/cip/Lot/openLotListColumns';
import { AssetClassBadge } from '@/components/AssetClassBadge';
import { ServerDataTable } from '@/components/table/DataTable';
import { TradeSideBadge } from '@/components/TradeSideBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRequiredParams } from 'utils';

import { AccountNavCash } from './AccountNavCash';
import {
    accountTableClassName,
    AccountTableLayout,
    DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
} from './accountTableDefaults';
import { AccountTableToolbar } from './AccountTableToolbar';
import { LargerFlagCell } from './LargerFlagCell';
import { HoldingsViewToggle } from './new-account/AccountHoldingsTab';
import { HOLDINGS_OPEN_LOT_COLUMNS, holdingsColumns, holdingsColumnVisibility } from './new-account/holdings-columns';
import { isPnlMoneyColumn, isPnlPercentColumn, PnlMoneyCell, PnlPercentCell } from './pnlCellRenderers';
import { useAccountTableState } from './useAccountTableState';

// visible: symbol, description, assetClass, quantity, closePrice, grossExposure, costBasis, pnlUnrealized, returnUnrealized, term, openDate
const OPEN_LOTS_DEFAULT_COLUMN_VISIBILITY = {
    ...DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
    assetType: false,
    side: false,
    netExposure: false,
    pnlRealized: false,
    returnRealized: false,
    primaryExchange: false,
    cusip: false,
    isin: false,
    subAssetType: false,
    allocationType: false,
    investmentSleeve: false,
    maturityDate: false,
    gicsCode: false,
    gicsSector: false,
    gicsIndustry: false,
};

// visible: symbol, description, assetClass, quantity, tradeDate, tradePrice, costBasis, pnlRealized, returnRealized
const CLOSED_LOTS_DEFAULT_COLUMN_VISIBILITY = {
    ...DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
    tradeDate: true, // override base (hidden by default) — represents the close date
    assetType: false,
    subAssetType: false,
    side: false,
    netExposure: false,
    allocationType: false,
    investmentAlgo: false,
    investmentSleeve: false,
    scId: false,
    coId: false,
    priceMultiplier: false,
    contractMultiplier: false,
    primaryExchange: false,
    cusip: false,
    isin: false,
    gicsCode: false,
    gicsSector: false,
    gicsIndustry: false,
    closedLotId: false,
};

const EXCLUDE_OPEN_LOT_COLUMNS = ['pId', 'portfolioGuid', 'portfolioName', 'householdName', 'aC', 'referencePortfolio'];
const EXCLUDE_CLOSED_LOT_COLUMNS = [
    'pId',
    'portfolioGuid',
    'portfolioName',
    'householdName',
    'aC',
    'referencePortfolio',
];

const HOLDINGS_OPEN_LOT_VISIBILITY = holdingsColumnVisibility(openLotListColumns, HOLDINGS_OPEN_LOT_COLUMNS);

export const AccountLotsTab = ({ layout = 'legacy' }: { layout?: AccountTableLayout }) => {
    const holdings = layout === 'new';
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { accountGUID } = useRequiredParams(['accountGUID']);
    const { lotType } = useParams<{ lotType: string }>();

    // Get the current active tab from the URL parameter
    const getCurrentTab = () => {
        if (holdings) return 'open';
        if (lotType === 'open' || lotType === 'closed') {
            return lotType;
        }
        // Default to open if no valid lotType found
        return 'open';
    };

    const {
        columnVisibility: openLotsColumnVisibility,
        setColumnVisibility: setOpenLotsColumnVisibility,
        columnOrder: openLotsColumnOrder,
        setColumnOrder: setOpenLotsColumnOrder,
        columnSizing: openLotsColumnSizing,
        setColumnSizing: setOpenLotsColumnSizing,
        resetSettings: resetOpenLotsSettings,
        pagination: openLotsPagination,
        setPagination: setOpenLotsPagination,
        sorting: openLotsSorting,
        setSorting: setOpenLotsSorting,
        columnFilters: openLotsColumnFilters,
        setColumnFilters: setOpenLotsColumnFilters,
        accountFilter: openLotsAccountFilter,
        allFilters: openLotsAllFilters,
    } = useAccountTableState({
        tableId: holdings ? 'accountHoldingsOpenLots' : 'accountOpenLot',
        accountGUID,
        excludeColumns: EXCLUDE_OPEN_LOT_COLUMNS,
        defaultColumnVisibility: holdings ? HOLDINGS_OPEN_LOT_VISIBILITY : OPEN_LOTS_DEFAULT_COLUMN_VISIBILITY,
        defaultColumnOrder: holdings ? HOLDINGS_OPEN_LOT_COLUMNS : DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    });

    const {
        columnVisibility: closedLotsColumnVisibility,
        setColumnVisibility: setClosedLotsColumnVisibility,
        columnOrder: closedLotsColumnOrder,
        setColumnOrder: setClosedLotsColumnOrder,
        columnSizing: closedLotsColumnSizing,
        setColumnSizing: setClosedLotsColumnSizing,
        resetSettings: resetClosedLotsSettings,
        pagination: closedLotsPagination,
        setPagination: setClosedLotsPagination,
        sorting: closedLotsSorting,
        setSorting: setClosedLotsSorting,
        columnFilters: closedLotsColumnFilters,
        setColumnFilters: setClosedLotsColumnFilters,
        accountFilter: closedLotsAccountFilter,
        allFilters: closedLotsAllFilters,
    } = useAccountTableState({
        tableId: 'accountClosedLot',
        accountGUID,
        excludeColumns: EXCLUDE_CLOSED_LOT_COLUMNS,
        defaultColumnVisibility: CLOSED_LOTS_DEFAULT_COLUMN_VISIBILITY,
        defaultColumnOrder: DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    });

    const openLotsColumnsWithCustomRenderers = useMemo(
        () =>
            (holdings ? holdingsColumns(openLotListColumns) : openLotListColumns).map((col) => {
                if (col.id === 'country') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <LargerFlagCell alpha3={getValue()} />,
                    };
                }
                if (col.id === 'assetClass') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => AssetClass }) => (
                            <AssetClassBadge assetClass={getValue()} />
                        ),
                    };
                }
                if (col.id === 'side') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <TradeSideBadge side={getValue()} />,
                    };
                }
                if (isPnlMoneyColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlMoneyCell value={getValue()} />
                        ),
                    };
                }
                if (isPnlPercentColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlPercentCell value={getValue()} />
                        ),
                    };
                }

                return col;
            }),
        [holdings],
    );

    const closedLotsColumnsWithCustomRenderers = useMemo(
        () =>
            closedLotListColumns.map((col) => {
                if (col.id === 'country') {
                    return {
                        ...col,
                        cell: ({ getValue }: any) => <LargerFlagCell alpha3={getValue()} />,
                    };
                }
                if (col.id === 'assetClass') {
                    return {
                        ...col,
                        cell: ({ getValue }: any) => <AssetClassBadge assetClass={getValue()} />,
                    };
                }
                if (isPnlMoneyColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlMoneyCell value={getValue()} />
                        ),
                    };
                }
                if (isPnlPercentColumn(col.id)) {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => number | undefined }) => (
                            <PnlPercentCell value={getValue()} />
                        ),
                    };
                }
                return col;
            }),
        [],
    );

    const {
        data: openLotsPage,
        isLoading: isOpenLotsLoading,
        isFetching: isOpenLotsFetching,
    } = useOpenLots({
        columnFilters: openLotsAllFilters,
        pagination: openLotsPagination,
        sorting: openLotsSorting,
    });

    const {
        data: closedLotsPage,
        isLoading: isClosedLotsLoading,
        isFetching: isClosedLotsFetching,
    } = useClosedLots({
        enabled: !holdings,
        columnFilters: closedLotsAllFilters,
        pagination: closedLotsPagination,
        sorting: closedLotsSorting,
    });

    return (
        <Tabs
            value={getCurrentTab()}
            onValueChange={(value) => navigate(`${pathname.slice(0, pathname.lastIndexOf('/'))}/${value}`)}
        >
            <TabsList className={holdings ? 'hidden' : undefined}>
                <TabsTrigger value="open">Open Lots</TabsTrigger>
                <TabsTrigger value="closed">Closed Lots</TabsTrigger>
            </TabsList>

            <TabsContent value="open" className={holdings ? 'pt-0' : 'mt-4'}>
                <ServerDataTable
                    data={openLotsPage?.lots || []}
                    columns={openLotsColumnsWithCustomRenderers}
                    state={{
                        pagination: openLotsPagination,
                        sorting: openLotsSorting,
                        columnFilters: openLotsColumnFilters,
                        columnVisibility: openLotsColumnVisibility,
                        columnOrder: openLotsColumnOrder,
                        columnSizing: openLotsColumnSizing,
                    }}
                    onPaginationChange={setOpenLotsPagination}
                    onSortingChange={setOpenLotsSorting}
                    onColumnFiltersChange={setOpenLotsColumnFilters}
                    onColumnVisibilityChange={setOpenLotsColumnVisibility}
                    onColumnOrderChange={setOpenLotsColumnOrder}
                    onColumnSizingChange={setOpenLotsColumnSizing}
                    isLoading={isOpenLotsLoading}
                    isFetching={isOpenLotsFetching}
                    emptyMessage="No open lots found"
                    className={accountTableClassName(layout)}
                    rowCount={openLotsPage?.totalNr ?? 0}
                    deferInitialRender={true}
                    renderToolbar={(table) => (
                        <AccountTableToolbar
                            table={table}
                            resourceType={ResourceType.LOT}
                            layout={layout}
                            extraControls={<HoldingsViewToggle />}
                            metaData={{ lotType: LotType.OPEN }}
                            additionalFilters={openLotsAccountFilter}
                            excludeColumns={EXCLUDE_OPEN_LOT_COLUMNS}
                            onReset={resetOpenLotsSettings}
                            leftContent={<AccountNavCash accountGUID={accountGUID} />}
                        />
                    )}
                />
            </TabsContent>

            {!holdings && (
                <TabsContent value="closed" className="mt-4">
                    <ServerDataTable
                        data={closedLotsPage?.lots || []}
                        columns={closedLotsColumnsWithCustomRenderers}
                        state={{
                            pagination: closedLotsPagination,
                            sorting: closedLotsSorting,
                            columnFilters: closedLotsColumnFilters,
                            columnVisibility: closedLotsColumnVisibility,
                            columnOrder: closedLotsColumnOrder,
                            columnSizing: closedLotsColumnSizing,
                        }}
                        onPaginationChange={setClosedLotsPagination}
                        onSortingChange={setClosedLotsSorting}
                        onColumnFiltersChange={setClosedLotsColumnFilters}
                        onColumnVisibilityChange={setClosedLotsColumnVisibility}
                        onColumnOrderChange={setClosedLotsColumnOrder}
                        onColumnSizingChange={setClosedLotsColumnSizing}
                        isLoading={isClosedLotsLoading}
                        isFetching={isClosedLotsFetching}
                        emptyMessage="No closed lots found"
                        className="rounded-md border"
                        rowCount={closedLotsPage?.totalNr ?? 0}
                        deferInitialRender={true}
                        renderToolbar={(table) => (
                            <AccountTableToolbar
                                table={table}
                                resourceType={ResourceType.LOT}
                                metaData={{ lotType: LotType.CLOSED }}
                                additionalFilters={closedLotsAccountFilter}
                                excludeColumns={EXCLUDE_CLOSED_LOT_COLUMNS}
                                onReset={resetClosedLotsSettings}
                                leftContent={<AccountNavCash accountGUID={accountGUID} />}
                            />
                        )}
                    />
                </TabsContent>
            )}
        </Tabs>
    );
};
